import React, { useState } from "react";
import axios from "axios";
import { X, CheckCircle2, MessageCircle } from "lucide-react";
import { company } from "../mock";
import { trackConversion, CONV } from "../lib/gtag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/catalog`;
const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><rect width='120' height='120' fill='#eef2f6'/><text x='50%' y='50%' font-family='Arial' font-size='12' fill='#94a3b8' text-anchor='middle' dominant-baseline='middle'>DEA</text></svg>"
  );

const fmtPrice = (n) => (n == null ? "-" : "€ " + n.toLocaleString("it-IT"));

export default function EngineQuoteForm({ engine, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", note: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  if (!engine) return null;

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: null })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Inserisci nome e cognome / officina";
    if (!form.phone.trim()) e.phone = "Inserisci un numero di telefono";
    else if (!/^[+]?[\d\s().-]{6,}$/.test(form.phone.trim())) e.phone = "Numero non valido";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Email non valida";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildWaMessage = () => {
    const lines = [
      `Ciao ${company.name}, vorrei un preventivo per questo motore:`,
      `• Motore: ${engine.titolo}`,
      engine.codice_motore ? `• Codice: ${engine.codice_motore}` : null,
      `• Prezzo: ${fmtPrice(engine.prezzo_vendita)}`,
      "",
      `Nome/Officina: ${form.name}`,
      `Telefono: ${form.phone}`,
      form.email ? `Email: ${form.email}` : null,
      form.note ? `Note: ${form.note}` : null,
    ].filter((l) => l !== null);
    return encodeURIComponent(lines.join("\n"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        note: form.note.trim() || null,
        engine_id: engine.id,
        engine_title: engine.titolo,
        engine_code: engine.codice_motore || null,
        engine_price: engine.prezzo_vendita || null,
      });
    } catch (err) {
      // Even if saving fails we still let the user reach WhatsApp
      console.error("Lead save failed", err);
    } finally {
      setSending(false);
      setDone(true);
      trackConversion(CONV.preventivo);
    }
  };

  const openWhatsApp = () => {
    trackConversion(CONV.contatto);
    window.open(`https://wa.me/${company.wa}?text=${buildWaMessage()}`, "_blank");
    onClose();
  };

  return (
    <div className="engine-modal-overlay" onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}>
      <div className="wa-modal" style={{ maxWidth: 480 }}>
        <button className="wa-modal-close" onClick={onClose} aria-label="Chiudi"><X size={18} /></button>

        {!done ? (
          <>
            <h2 className="wa-modal-h2">Richiedi preventivo</h2>
            <div className="eq-form-summary">
              <img src={engine.foto_url || FALLBACK_IMG} alt={engine.titolo}
                onError={(ev) => { ev.target.src = FALLBACK_IMG; }} />
              <div>
                <div className="eq-t">{engine.titolo}</div>
                <div className="eq-p">{fmtPrice(engine.prezzo_vendita)}</div>
              </div>
            </div>

            <form onSubmit={submit} noValidate>
              <div className="eq-field">
                <label>Nome e Cognome / Officina <span className="req">*</span></label>
                <input className={errors.name ? "error" : ""} value={form.name}
                  onChange={(e) => set("name", e.target.value)} placeholder="Es. Mario Rossi / Autofficina Rossi" />
                {errors.name && <div className="err-msg">{errors.name}</div>}
              </div>
              <div className="eq-field">
                <label>Telefono <span className="req">*</span></label>
                <input className={errors.phone ? "error" : ""} value={form.phone} inputMode="tel"
                  onChange={(e) => set("phone", e.target.value)} placeholder="Es. 333 1234567" />
                {errors.phone && <div className="err-msg">{errors.phone}</div>}
              </div>
              <div className="eq-field">
                <label>Email <span style={{ color: "var(--grey)", fontWeight: 400 }}>(opzionale)</span></label>
                <input className={errors.email ? "error" : ""} type="email" value={form.email}
                  onChange={(e) => set("email", e.target.value)} placeholder="La tua email" />
                {errors.email && <div className="err-msg">{errors.email}</div>}
              </div>
              <div className="eq-field">
                <label>Note / Dettagli <span style={{ color: "var(--grey)", fontWeight: 400 }}>(opzionale)</span></label>
                <textarea rows={3} value={form.note} onChange={(e) => set("note", e.target.value)}
                  placeholder="Codice P5, allestimento, urgenza, domande…" />
              </div>
              <p className="eq-privacy">Inviando accetti il trattamento dei dati per la sola risposta alla richiesta.</p>
              <button type="submit" className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center" }} disabled={sending}>
                <MessageCircle size={18} /> {sending ? "Invio…" : "Invia richiesta"}
              </button>
            </form>
          </>
        ) : (
          <div className="eq-success">
            <div className="icon"><CheckCircle2 size={36} color="#16a34a" /></div>
            <h3>Richiesta registrata!</h3>
            <p>Grazie {form.name.split(" ")[0]}! Abbiamo salvato la tua richiesta per <strong>{engine.titolo}</strong>. Prosegui su WhatsApp per inviarcela subito e ricevere risposta in tempi rapidi.</p>
            <button className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center" }} onClick={openWhatsApp}>
              <MessageCircle size={18} /> Continua su WhatsApp
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
